// POS arithmetic only. Never evaluate a string as JavaScript.
// A trailing percentage is a fraction; for addition/subtraction a simple
// percentage is relative to the running amount (e.g. 200 + 10% = 220).
export function calculateExpression(expression) {
  if (typeof expression !== "string" || !expression.trim() || expression.length > 160) {
    throw new Error("Enter a calculation.");
  }
  const source = expression.replace(/×/g, "*").replace(/÷/g, "/").replace(/−/g, "-").replace(/\s/g, "");
  let index = 0;
  const peek = () => source[index];
  const take = (value) => peek() === value && (index++, true);
  const fail = () => { throw new Error("Check the calculation."); };
  function primary() {
    if (take("(")) {
      const result = addition();
      if (!take(")")) fail();
      return result;
    }
    const match = source.slice(index).match(/^(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?/i);
    if (!match) fail();
    index += match[0].length;
    let value = Number(match[0]);
    let percent = false;
    if (take("%")) { value /= 100; percent = true; }
    return { value, percent };
  }
  function unary() {
    if (take("+")) return unary();
    if (take("-")) { const value = unary(); return { value: -value.value, percent: value.percent }; }
    return primary();
  }
  function multiply() {
    let left = unary();
    while (peek() === "*" || peek() === "/") {
      const op = source[index++];
      const right = unary();
      if (op === "/" && right.value === 0) throw new Error("Cannot divide by zero.");
      left = { value: op === "*" ? left.value * right.value : left.value / right.value, percent: false };
    }
    return left;
  }
  function addition() {
    let left = multiply();
    while (peek() === "+" || peek() === "-") {
      const op = source[index++];
      const right = multiply();
      const amount = right.percent ? left.value * right.value : right.value;
      left = { value: op === "+" ? left.value + amount : left.value - amount, percent: false };
    }
    return left;
  }
  const answer = addition().value;
  if (index !== source.length) fail();
  if (!Number.isFinite(answer)) throw new Error("Result is too large.");
  // Floating-point noise is not useful for POS calculations.
  return Number(answer.toPrecision(13));
}

export function formatCalculatorNumber(value) {
  if (!Number.isFinite(value)) return "Error";
  const readable = Number(value.toPrecision(12));
  if (readable !== 0 && (Math.abs(readable) <= 0.000000000001 || Math.abs(readable) >= 1e15)) {
    return readable.toExponential(8);
  }
  return readable.toLocaleString("en-US", { maximumFractionDigits: 12 });
}

export function appendCalculatorInput(expression, key, justEvaluated = false) {
  if (key === "AC") return "";
  if (key === "BACK") return justEvaluated ? "" : expression.slice(0, -1);
  if (key === "SIGN") {
    if (!expression) return "-";
    const negative = expression.match(/\(-((?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?%?)\)$/i);
    if (negative) return expression.slice(0, -negative[0].length) + negative[1];
    const trailing = expression.match(/((?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?%?)$/i);
    if (trailing) return expression.slice(0, -trailing[0].length) + "(-" + trailing[0] + ")";
    return expression;
  }
  const fresh = justEvaluated && (key === "." || /^\d$/.test(key));
  const current = fresh ? "" : expression;
  if (/^\d$/.test(key)) {
    if (current.length >= 120 || current.endsWith("%") || current.endsWith(")")) return current;
    return current + key;
  }
  if (key === ".") {
    if (current.endsWith("%") || current.endsWith(")")) return current;
    const last = current.split(/[+×÷−()]/).pop() || "";
    if (last.includes(".")) return current;
    return current + (last === "" ? "0." : ".");
  }
  if (key === "%") {
    if (/\d$/.test(current)) return current + "%";
    return current;
  }
  if (["+", "−", "×", "÷"].includes(key)) {
    if (!current) return key === "−" ? "−" : "";
    if (/[+−×÷.]$/.test(current)) return current.replace(/[+−×÷.]$/, key);
    return current + key;
  }
  return current;
}
